import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MatterStatus,
  RetainerStatus,
  type CreateMatterInput,
  type ExpertQuery,
} from '~/gql/graphql';
import { matterHasActiveExpertParticipation } from '~/modules/matters/matterCardUtils';
import type {
  MatterViewModel,
  MyMatterExpertsRow,
} from '~/modules/matters/matters.types';
import { useMattersQuery } from '~/modules/matters/useMattersQuery';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';
import { useMyMatterExpertsQuery } from '~/modules/matters/useMyMatterExpertsQuery';
import type { CreateMatterFormData } from '~/modules/matters/modals/CreateMatterModal';
import { ExpertDocument } from '~/graphql/queries/expert';
import { useToast } from '~/shared/contexts/ToastContext';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import {
  UserRole,
  type CertificateLicenseRow,
  type ProfessionalExperienceRow,
  type PublicationRow,
  type User,
} from '~/shared/types/connect';

export enum ExpertProfileAccessState {
  Error = 'error',
  NotFound = 'not-found',
  Ok = 'ok',
}

type ExpertQueryExpert = NonNullable<ExpertQuery['expert']>;

function mapExpertResponseToUser(expert: ExpertQueryExpert): User {
  const certificatesLicenses: CertificateLicenseRow[] = [];
  for (const cert of expert.certifications ?? []) {
    certificatesLicenses.push({
      name: cert.name,
      issuingBody: cert.board,
      date: String(cert.year),
    });
  }
  for (const license of expert.licenses ?? []) {
    certificatesLicenses.push({
      name: license.name,
      issuingBody: license.issuer,
      date: '—',
    });
  }
  const professionalExperience: ProfessionalExperienceRow[] = (
    expert.professionalExperience ?? []
  ).map((professionalExperience) => ({
    role: professionalExperience.role,
    organization: professionalExperience.institution,
    period: `${professionalExperience.startYear} – ${
      professionalExperience.endYear ?? 'Present'
    }`,
    description: professionalExperience.summary,
  }));
  const publications: PublicationRow[] = (expert.publications ?? []).map(
    (publication) => ({
      title: publication.topic,
      journal: publication.publication,
    }),
  );
  return {
    id: expert.id,
    email: expert.email,
    name: expert.name,
    role: UserRole.EXPERT,
    avatarUrl: expert.avatarUrl ?? undefined,
    hourlyRate:
      expert.hourlyRate !== null && expert.hourlyRate !== undefined
        ? Number(expert.hourlyRate)
        : undefined,
    linkedInUrl: expert.linkedinProfileUrl ?? undefined,
    previouslyTestified: expert.previouslyTestified,
    professionalSummary: expert.professionalSummary ?? undefined,
    industry: expert.industries?.length
      ? expert.industries.map((industry) => industry.name).join(', ')
      : undefined,
    specialty: expert.specialties?.length
      ? expert.specialties.map((specialty) => specialty.name).join(', ')
      : undefined,
    subspecialty: expert.subspecialties?.length
      ? expert.subspecialties.map((specialty) => specialty.name).join(', ')
      : undefined,
    statesOfPractice: expert.statesOfPractice?.length
      ? expert.statesOfPractice.map((state) => state.name).join(', ')
      : undefined,
    yearsExperience: expert.yearsExperience ?? undefined,
    credentials: expert.credentials?.length
      ? expert.credentials
          .map(
            (cred) =>
              `${cred.name}${cred.institution ? `, ${cred.institution}` : ''}${
                cred.year ? ` (${cred.year})` : ''
              }`,
          )
          .join('; ')
      : undefined,
    certifications: (expert.certifications ?? []).map((cert) => cert.name),
    certificatesLicenses:
      certificatesLicenses.length > 0 ? certificatesLicenses : undefined,
    professionalExperience:
      professionalExperience.length > 0 ? professionalExperience : undefined,
    publications: publications.length > 0 ? publications : undefined,
  };
}

export interface UseExpertProfilePageReturn {
  accessState: ExpertProfileAccessState;
  navigate: ReturnType<typeof useNavigate>;
  expert: User | undefined;
  isLoading: boolean;
  error: Error | null;
  isOwnProfile: boolean;
  currentUser: User | null;
  mattersWithoutExpert: MatterViewModel[];
  isCreateMatterModalOpen: boolean;
  setIsCreateMatterModalOpen: (open: boolean) => void;
  isAddToMatterModalOpen: boolean;
  setIsAddToMatterModalOpen: (open: boolean) => void;
  openAddToMatterModal: () => void;
  lastActive: string | null;
  handleCreateMatter: (data: CreateMatterFormData) => void;
  isCreatingMatter: boolean;
  experts: MyMatterExpertsRow[];
  /** Only set when isOwnProfile; used for Collaboration Summary. */
  collaborationSummary: {
    totalMatters: number;
    activeMatters: number;
    totalEarned: number;
  } | null;
}

export function useExpertProfilePage(): UseExpertProfilePageReturn {
  const navigate = useNavigate();
  const { expertId } = useParams();
  const { showToast } = useToast();
  const { user: currentUser, isLoading: isCurrentUserLoading } =
    useCurrentUser();
  const { matters } = useMattersQuery();
  const { createMatter: createMatterMutation, isCreating: isCreatingMatter } =
    useMatterMutations();
  const { experts: pickerExperts } = useMyMatterExpertsQuery();

  const { data, loading, error } = useQuery(ExpertDocument, {
    variables: expertId ? { expertId } : undefined,
    skip: !expertId,
  });

  const [isCreateMatterModalOpen, setIsCreateMatterModalOpen] = useState(false);
  const [isAddToMatterModalOpen, setIsAddToMatterModalOpen] = useState(false);

  const openAddToMatterModal = () => {
    setIsAddToMatterModalOpen(true);
  };

  const expert: User | undefined = data?.expert
    ? mapExpertResponseToUser(data.expert)
    : undefined;

  const experts = useMemo((): MyMatterExpertsRow[] => {
    if (!expert) {
      return pickerExperts;
    }
    if (pickerExperts.some((entry) => entry.id === expert.id)) {
      return pickerExperts;
    }
    return [
      ...pickerExperts,
      {
        id: expert.id,
        name: expert.name,
        email: expert.email,
        avatarUrl: expert.avatarUrl ?? '',
      },
    ];
  }, [pickerExperts, expert]);

  const isOwnProfile = Boolean(
    currentUser?.role === UserRole.EXPERT &&
      expertId === currentUser.id &&
      expert,
  );

  let myMattersWithExpert: MatterViewModel[] = [];
  if (expert && currentUser && isOwnProfile) {
    myMattersWithExpert = matters.filter((matter) =>
      matterHasActiveExpertParticipation(matter, expert.id),
    );
  } else if (expert && currentUser && currentUser.role === UserRole.ATTORNEY) {
    myMattersWithExpert = matters.filter(
      (matter) =>
        matter.attorneyId === currentUser.id &&
        matterHasActiveExpertParticipation(matter, expert.id),
    );
  }

  const mattersWithoutExpert: MatterViewModel[] =
    currentUser?.role === UserRole.ATTORNEY && expert && !isOwnProfile
      ? matters.filter(
          (matter) =>
            matter.attorneyId === currentUser.id &&
            matter.status !== MatterStatus.Closed &&
            !matterHasActiveExpertParticipation(matter, expert.id),
        )
      : [];

  const lastActive =
    myMattersWithExpert.length > 0
      ? myMattersWithExpert.reduce(
          (newest, matter) =>
            matter.createdAt > newest ? matter.createdAt : newest,
          myMattersWithExpert[0].createdAt,
        ) || null
      : null;

  const handleCreateMatter = async (data: CreateMatterFormData) => {
    if (!currentUser || !expert) {
      return;
    }
    const input: CreateMatterInput = {
      title: data.title,
      caseDescription: data.caseDescription,
      caseNumber: data.caseNumber || undefined,
      courtName: data.courtName || undefined,
      location: data.location || undefined,
      keyDates: data.keyDates || undefined,
      nextDeadline: data.nextDeadline || undefined,
      travelPlans: data.travelPlans || undefined,
      writtenReportNeeded: data.writtenReportNeeded || undefined,
      expertId: data.expertId || undefined,
    };
    try {
      const matter = await createMatterMutation(input);
      setIsCreateMatterModalOpen(false);
      if (matter?.id) {
        navigate(`/matters/${matter.id}`);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'create_matter_expert_profile' },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
      throw error;
    }
  };

  const collaborationSummary =
    isOwnProfile && expert
      ? (() => {
          const myMatters = matters.filter((matter) =>
            matterHasActiveExpertParticipation(matter, expert.id),
          );
          const activeMatters = myMatters.filter(
            (matter) => matter.status === MatterStatus.Active,
          ).length;
          const retainersForExpert = myMatters.flatMap((matter) =>
            matter.matterExperts
              .filter((row) => row.expertId === expert.id)
              .flatMap((row) => row.retainers),
          );
          const totalEarned =
            retainersForExpert
              .filter((retainer) => retainer.status === RetainerStatus.Approved)
              .reduce((sum, retainer) => sum + retainer.subtotal, 0) / 100;
          return {
            totalMatters: myMatters.length,
            activeMatters,
            totalEarned,
          };
        })()
      : null;

  let accessState: ExpertProfileAccessState = ExpertProfileAccessState.Ok;
  if (!loading && !isCurrentUserLoading) {
    if (error) {
      accessState = ExpertProfileAccessState.Error;
    } else if (!expertId || !expert) {
      accessState = ExpertProfileAccessState.NotFound;
    }
  }

  return {
    accessState,
    navigate,
    expert,
    isLoading: loading || isCurrentUserLoading,
    error: error ?? null,
    isOwnProfile,
    currentUser,
    mattersWithoutExpert,
    isCreateMatterModalOpen,
    setIsCreateMatterModalOpen,
    isAddToMatterModalOpen,
    setIsAddToMatterModalOpen,
    openAddToMatterModal,
    lastActive,
    handleCreateMatter,
    isCreatingMatter,
    experts,
    collaborationSummary,
  };
}
